import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch, shallowEqual, Provider } from "react-redux";
import { Layer, Image, Stage } from "react-konva";
import { v4 as uuidv4 } from "uuid";
import {
  setActivePolygonIndex,
  setPolygons,
} from "../store/slices/polygonSlice";
import { initStore } from "../store";
import Polygon from "./Polygon";

export const Canvas = ({
  imageSource,
  maxPolygons = 1,
  polygonStyle,
  imageSize,
  initialPolygonsLength,
  showLabel = false,
  setIsRoiEdited,
  action,
  setIsFinished,
  isFinishedROI,
  readOnly = false
}) => {
  const dispatch = useDispatch();
  const [image, setImage] = useState();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [isMouseOverPoint, setIsMouseOverPoint] = useState(false);
  const isMouseOverPointRef = useRef(false);
  const { polygons, activePolygonIndex } = useSelector(
    (state) => state.polygon.present,
    shallowEqual
  )
  const [originalWidth, originalHeight] = imageSize;

  const imageElement = useMemo(() => {
    const element = new window.Image();
    element.src = imageSource;
    return element;
  }, [imageSource]);

  useEffect(() => {
    setImageLoaded(false);
    const onload = function() {
      if (originalWidth && originalHeight) {
        setSize({
          width: originalWidth,
          height: originalHeight
        })
      } else {
        setSize({
          width: imageElement.width,
          height: imageElement.height,
        });
      }
      setImage(imageElement);
      setImageLoaded(true);
    };
    if (imageElement.complete && imageElement.naturalWidth > 0) {
      onload();
    } else {
      imageElement.addEventListener("load", onload);
    }
    
    return () => {
      imageElement.removeEventListener("load", onload)
    }
  }, [imageElement, originalWidth, originalHeight])

  const getMousePos = stage => {
    return [
      stage.getPointerPosition()?.x ?? 0,
      stage.getPointerPosition()?.y ?? 0,
    ];
  };
  const handleMouseClick = useCallback(
    (e) => {
      let activeKey = activePolygonIndex;
      const copy = [...polygons];
      // prevent adding new polygon if maxPolygons is reached
      if (copy.filter((p) => p.isFinished).length >= maxPolygons) return;

      let polygon = copy[activeKey];
      if (!polygon) {
        return;
      }
      const { isFinished } = polygon;
      // prevent adding new point on vertex if it is not mouse over
      if (e.target.name() === "vertex" && !isMouseOverPointRef.current) {
        return;
      }
      if (isFinished) {
        // create new polygon
        polygon = {
          id: uuidv4(),
          points: [],
          flattenedPoints: [],
          isFinished: false,
          label: `Polygon ${copy.length + 1}`,
          vertexRadius: polygonStyle.vertexRadius,
          lineColor: polygonStyle.lineColor,
          fillColor: polygonStyle.fillColor,
          vertexColor: polygonStyle.vertexColor,
          vertexStrokeWidth: polygonStyle.vertexStrokeWidth,
        };
        isMouseOverPointRef.current = false;
        setIsMouseOverPoint(false);
        activeKey += 1;
        dispatch(setActivePolygonIndex(activeKey));
      }
      const { points } = polygon;
      const stage = e.target.getStage();
      const mousePos = getMousePos(stage);
      if (isMouseOverPointRef.current && points.length >= 3) {
        polygon = {
          ...polygon,
          isFinished: true,
        };
        setIsFinished(!isFinishedROI ? true : false)
      } else {
        polygon = {
          ...polygon,
          points: [...points, mousePos],
        };
      }
      copy[activeKey] = polygon;
      dispatch(setPolygons({ polygons: copy, shouldUpdateHistory: true }));
    },
    [activePolygonIndex, dispatch, maxPolygons, polygons]
  );

  const handleMouseMove = useCallback(
    (e) => {
      const stage = e.target.getStage();
      if (!stage) {
        return;
      }
      const mousePos = getMousePos(stage);
      // // set flattened points for active polygon
      const copy = [...polygons];
      let polygon = copy[activePolygonIndex];
      if (!polygon) {
        return;
      }
      const { points, isFinished } = polygon;
      if (isFinished) {
        //setIsFinished(true)
        return;
      }
      const _flattenedPoints = points
        .concat(mousePos)
        .reduce((a, b) => a.concat(b), []);
      polygon = {
        ...polygon,
        flattenedPoints: _flattenedPoints,
      };
      copy[activePolygonIndex] = polygon;
      dispatch(setPolygons({ polygons: copy, shouldUpdateHistory: false }));
    },
    [activePolygonIndex, dispatch, polygons]
  );

  const handleMouseOverStartPoint = useCallback(
    (e, polygonKey) => {
      const polygon = polygons[polygonKey];
      const { points, isFinished } = polygon;
      if (isFinished || points.length < 3) {
        return;
      }
      e.target.scale({ x: 3, y: 3 });
      isMouseOverPointRef.current = true;
      setIsMouseOverPoint(true);
    },
    [polygons]
  );

  const handleMouseOutStartPoint = useCallback((e) => {
    e.target.scale({ x: 1, y: 1 });
    isMouseOverPointRef.current = false;
    setIsMouseOverPoint(false);
  }, []);

  const handlePointDragMove = useCallback(
    (e, polygonKey) => {
      if (readOnly) {
        e.target.stopDrag();
        return;
      }
      const copy = [...polygons];
      let polygon = copy[polygonKey];
      const { isFinished } = polygon;
      if (!isFinished) {
        // prevent drag:
        e.target.stopDrag();
        return;
      }
      const stage = e.target.getStage();
      const index = e.target.index - 1;
      const pos = [e.target.x(), e.target.y()];
      if (stage) {
        if (pos[0] < 0) pos[0] = 0;
        if (pos[1] < 0) pos[1] = 0;
        if (pos[0] > stage.width()) pos[0] = stage.width();
        if (pos[1] > stage.height()) pos[1] = stage.height();
      }

      const { points } = polygon;
      const newPoints = [
        ...points.slice(0, index),
        pos,
        ...points.slice(index + 1),
      ];
      const flattenedPoints = newPoints.reduce((a, b) => a.concat(b), []);
      polygon = {
        ...polygon,
        points: newPoints,
        flattenedPoints,
      };
      copy[polygonKey] = polygon;
      dispatch(setPolygons({ polygons: copy, shouldUpdateHistory: false }));
    },
    [dispatch, readOnly, polygons]
  );

  const handlePointDragEnd = useCallback(
    (e, polygonKey) => {
      if (readOnly) return;
      const index = e.target.index - 1;
      const pos = [e.target.x(), e.target.y()];
      const copy = [...polygons];
      let polygon = copy[polygonKey];
      const { points } = polygon;
      const newPoints = [
        ...points.slice(0, index),
        pos,
        ...points.slice(index + 1),
      ];
      const flattenedPoints = newPoints.reduce((a, b) => a.concat(b), []);
      polygon = {
        ...polygon,
        points: newPoints,
        flattenedPoints,
      };
      copy[polygonKey] = polygon;
      setIsRoiEdited(action == "update" ? true : false);
      dispatch(setPolygons({ polygons: copy, shouldUpdateHistory: true }));
    },
    [dispatch, readOnly, polygons]
  );

  const handleTouchStart = useCallback(
    (e) => {
      e.evt.preventDefault();
      handleMouseClick(e);
    },
    [handleMouseClick]
  );

  const handleTouchMove = useCallback(
    (e) => {
      e.evt.preventDefault();
      handleMouseMove(e);
    },
    [handleMouseMove]
  );

  const handleGroupDragEnd = useCallback(
    (e, polygonKey) => {
      if (readOnly) {
        e.target.position({ x: 0, y: 0 });
        return;
      }
      //drag end listens other children circles' drag end event
      //...for this 'name' attr is added
      const copy = [...polygons];
      let polygon = copy[polygonKey];
      const { points } = polygon;
      if (e.target.name() === "polygon") {
        const result = [];
        const copyPoints = [...points];
        copyPoints.forEach((point) =>
          result.push([point[0] + e.target.x(), point[1] + e.target.y()])
        );
        e.target.position({ x: 0, y: 0 }); //reset group position
        polygon = {
          ...polygon,
          points: result,
          flattenedPoints: result.reduce((a, b) => a.concat(b), []),
        };
        copy[polygonKey] = polygon;
        setIsRoiEdited(action == "update" ? true : false);
        dispatch(setPolygons({ polygons: copy, shouldUpdateHistory: true }));
      }
    },
    [dispatch, readOnly, polygons]
  );
  return (
    <Stage
      width={size.width}
      height={size.height}
      onMouseMove={handleMouseMove}
      onMouseDown={handleMouseClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
    >
      <Layer>
        <Image
          image={image}
          x={0}
          y={0}
          width={size.width}
          height={size.height}
        />
        {imageLoaded && polygons && polygons.map((polygon, index) => (
          <Polygon
            key={polygon.id}
            isFinished={polygon.isFinished}
            points={polygon.points}
            flattenedPoints={polygon.flattenedPoints}
            handlePointDragMove={(e) => handlePointDragMove(e, index)}
            handlePointDragEnd={(e) => handlePointDragEnd(e, index)}
            handleMouseOverStartPoint={(e) =>
              handleMouseOverStartPoint(e, index)
            }
            handleMouseOutStartPoint={handleMouseOutStartPoint}
            handleGroupDragEnd={(e) => handleGroupDragEnd(e, index)}
            polygonStyle={polygonStyle}
            showLabel={showLabel}
            label={polygon.label}
            vradius={polygon?.vertexRadius ? polygon.vertexRadius : ""}
            lcolor={polygon?.lineColor ? polygon.lineColor : ""}
            fcolor={polygon?.fillColor ? polygon.fillColor : ""}
            vcolor={polygon?.vertexColor ? polygon.vertexColor : ""}
            vwidth={polygon?.vertexStrokeWidth ? polygon.vertexStrokeWidth : ""}
            initialPolygonsData={initialPolygonsLength > index ? "yes" : "no"}
          />
        ))}
      </Layer>
    </Stage>
  );
};

export const PolygonAnnotation = ({
  bgImage,
  maxPolygons,
  initialPolygons,
  initialPolygonsLength,
  polygonStyle,
  imageSize,
  showLabel,
  children,
  setIsRoiEdited,
  action,
  setIsFinished,
  isFinishedROI,
  readOnly,
}) => {
  const store = useMemo(() => {
    return initStore(initialPolygons);
  }, [initialPolygons]);

  return (
    <Provider store={store}>
      <Canvas
        imageSource={bgImage}
        maxPolygons={maxPolygons}
        polygonStyle={polygonStyle}
        imageSize={imageSize}
        showLabel={showLabel}
        initialPolygonsLength={initialPolygonsLength}
        setIsRoiEdited={setIsRoiEdited}
        action={action}
        setIsFinished={setIsFinished}
        isFinishedROI={isFinishedROI}
        readOnly={readOnly}
      />
      {children}
    </Provider>
  );
};
